import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CONFIG } from '../config';

const DEFAULT_INSTRUCTION = (eventName) =>
  `You are a helpful assistant for ${eventName}. Answer guest questions concisely and helpfully based on the available knowledge base. If you cannot find an answer, say so honestly and suggest they contact the event organizers.`;

const STEP_LABELS = {
  bedrock_agent_alias: 'Bedrock Agent Alias',
  bedrock_agent: 'Bedrock Agent',
  bedrock_kb_data_source: 'Knowledge Base Data Source',
  bedrock_kb: 'Knowledge Base',
  s3_vectors_index: 'S3 Vectors Index',
  lex_bot_alias: 'Lex Bot Alias',
  lex_bot: 'Lex Bot',
  s3_kb_documents: 'S3 KB Documents',
  event_record: 'Event Record',
};

export default function AIConfigPage() {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [eventConfig, setEventConfig] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState(null);

  // Agent instruction state
  const [instruction, setInstruction] = useState('');
  const [savingInstruction, setSavingInstruction] = useState(false);
  const [instructionSuccess, setInstructionSuccess] = useState(null);
  const [instructionError, setInstructionError] = useState(null);

  // Provision state
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);

  // Deprovision state
  const [confirmDelete, setConfirmDelete] = useState(null); // 'lex' | 'bedrock' | 'all'
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Manual ID editing
  const [editingIds, setEditingIds] = useState(false);
  const [idFields, setIdFields] = useState({
    lex_bot_id: '',
    lex_bot_alias_id: '',
    lex_bot_locale_id: '',
    bedrock_agent_id: '',
    bedrock_agent_alias_id: '',
  });
  const [savingIds, setSavingIds] = useState(false);
  const [idsSuccess, setIdsSuccess] = useState(null);
  const [idsError, setIdsError] = useState(null);

  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => {
    loadInitialData();
    return stopPolling;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingEvents(true);
      setError(null);

      const [activeResp, eventsResp] = await Promise.all([
        fetch(`${CONFIG.apiBaseUrl}/active-event`, { credentials: 'include' }),
        fetch(`${CONFIG.apiBaseUrl}/admin/events`, { credentials: 'include' }),
      ]);

      let activeId = null;
      if (activeResp.ok) {
        const d = await activeResp.json();
        activeId = d.activeEventId;
      }
      let events = [];
      if (eventsResp.ok) {
        const d = await eventsResp.json();
        events = d.events || [];
      }

      setAvailableEvents(events);
      const defaultId = activeId || (events.length > 0 ? events[0].event_id : null);
      setSelectedEventId(defaultId);
      if (defaultId) await loadEventConfig(defaultId, events);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEventConfig = useCallback(async (id, events) => {
    try {
      setLoadingConfig(true);
      setError(null);
      setDeleteResult(null);
      setConfirmDelete(null);
      setProvisionError(null);
      setInstructionSuccess(null);
      setInstructionError(null);
      setIdsSuccess(null);
      setIdsError(null);
      setEditingIds(false);

      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.error || 'Failed to fetch event details');
      }
      const data = await resp.json();
      const cfg = data.event || data;
      setEventConfig(cfg);

      const eventsList = events || availableEvents;
      const eventName = cfg.event_name || eventsList.find(e => e.event_id === id)?.event_name || id;
      setInstruction(cfg.agent_instruction || DEFAULT_INSTRUCTION(eventName));
      setIdFields({
        lex_bot_id: cfg.lex_bot_id || '',
        lex_bot_alias_id: cfg.lex_bot_alias_id || '',
        lex_bot_locale_id: cfg.lex_bot_locale_id || '',
        bedrock_agent_id: cfg.bedrock_agent_id || '',
        bedrock_agent_alias_id: cfg.bedrock_agent_alias_id || '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingConfig(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEvents]);

  // Polling for provisioning status
  useEffect(() => {
    if (eventConfig?.ai_provisioning_status === 'pending') {
      pollTimerRef.current = setInterval(async () => {
        try {
          const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}`, {
            credentials: 'include',
          });
          if (!resp.ok) return;
          const data = await resp.json();
          const latest = data.event || data;
          if (latest.ai_provisioning_status !== 'pending') {
            setEventConfig(latest);
            setIdFields({
              lex_bot_id: latest.lex_bot_id || '',
              lex_bot_alias_id: latest.lex_bot_alias_id || '',
              lex_bot_locale_id: latest.lex_bot_locale_id || '',
              bedrock_agent_id: latest.bedrock_agent_id || '',
              bedrock_agent_alias_id: latest.bedrock_agent_alias_id || '',
            });
            stopPolling();
          }
        } catch { /* swallow poll errors */ }
      }, 5000);
    } else {
      stopPolling();
    }
    return stopPolling;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventConfig?.ai_provisioning_status, selectedEventId]);

  const handleEventChange = (newId) => {
    stopPolling();
    setSelectedEventId(newId);
    setEventConfig(null);
    loadEventConfig(newId);
  };

  // --- Provision ---
  const handleProvision = async (scope) => {
    setProvisioning(true);
    setProvisionError(null);
    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}/provision-ai`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scope }),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.detail || 'Failed to start provisioning');
      setEventConfig(prev => ({ ...prev, ai_provisioning_status: 'pending' }));
    } catch (err) {
      setProvisionError(err.message);
    } finally {
      setProvisioning(false);
    }
  };

  // --- Deprovision ---
  const handleDeprovision = async () => {
    const scope = confirmDelete;
    setConfirmDelete(null);
    setDeprovisioning(true);
    setDeleteResult(null);
    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}/deprovision-ai`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scope }),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || data.error || `Delete failed (${resp.status})`);
      setDeleteResult({ success: data.success, message: data.message, details: data.details, scope });
      await loadEventConfig(selectedEventId);
    } catch (err) {
      setDeleteResult({ success: false, error: err.message });
    } finally {
      setDeprovisioning(false);
    }
  };

  // --- Save Instruction ---
  const handleSaveInstruction = async () => {
    if (!selectedEventId || !instruction.trim()) return;
    try {
      setSavingInstruction(true);
      setInstructionError(null);
      setInstructionSuccess(null);
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}/agent-config`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ instruction: instruction.trim() }),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.detail || 'Failed to update instruction');
      setInstructionSuccess(data.message || 'Agent instruction updated successfully.');
      setEventConfig(prev => prev ? { ...prev, agent_instruction: instruction.trim() } : prev);
    } catch (err) {
      setInstructionError(err.message);
    } finally {
      setSavingInstruction(false);
    }
  };

  // --- Save IDs ---
  const handleSaveIds = async () => {
    try {
      setSavingIds(true);
      setIdsError(null);
      setIdsSuccess(null);
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}/ai-ids`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(idFields),
        }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.detail || 'Failed to update IDs');
      setIdsSuccess('AI IDs updated successfully.');
      // Refresh to reflect new status
      await loadEventConfig(selectedEventId);
      setEditingIds(false);
    } catch (err) {
      setIdsError(err.message);
    } finally {
      setSavingIds(false);
    }
  };

  // --- Helpers ---
  const statusBadge = (label, color) => (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
      fontSize: '0.8rem', fontWeight: 600, background: color, color: '#fff', marginRight: '8px',
    }}>
      {label}
    </span>
  );

  const provisionStatus = eventConfig?.ai_provisioning_status;
  const isPending = provisionStatus === 'pending';
  const hasLex = !!eventConfig?.lex_bot_id;
  const hasBedrock = !!eventConfig?.bedrock_agent_id;

  const stepColor = (s) => s === 'deleted' ? '#2e7d32' : s === 'not_found' || s === 'skipped' ? '#888' : '#c62828';

  const deleteConfirmText = {
    lex: `Delete the Lex bot and its alias for "${eventConfig?.event_name || selectedEventId}". Lex will be disabled. Bedrock Agent will remain.`,
    bedrock: `Delete the Bedrock Agent, Knowledge Base, S3 Vectors index, and all uploaded KB documents for "${eventConfig?.event_name || selectedEventId}". Lex bot will remain.`,
    all: `Delete ALL AI components (Lex bot, Bedrock Agent, Knowledge Base, S3 Vectors index, and KB documents) for "${eventConfig?.event_name || selectedEventId}". Lex will be disabled.`,
  };

  if (loadingEvents) {
    return (
      <div className="whatsapp-responses-page">
        <h2 className="page-title">AI Configuration</h2>
        <div className="loading-spinner">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">AI Configuration</h2>
      <p className="page-subtitle">Manage Lex bot and Bedrock Agent resources, instructions, and IDs per event.</p>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>Error: {error}</div>}

      {availableEvents.length === 0 && <div className="no-messages">No events found.</div>}

      {availableEvents.length > 0 && (
        <>
          {/* Event Selector */}
          <div className="settings-section">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="eventSelect" className="form-label">Select Event</label>
              <select
                id="eventSelect"
                value={selectedEventId || ''}
                onChange={(e) => handleEventChange(e.target.value)}
                className="form-input"
                disabled={loadingConfig || deprovisioning || provisioning}
              >
                {availableEvents.map((ev) => (
                  <option key={ev.event_id} value={ev.event_id}>
                    {ev.event_name || ev.event_id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingConfig && (
            <div className="settings-section">
              <div className="loading-spinner">Loading event details...</div>
            </div>
          )}

          {!loadingConfig && eventConfig && (
            <>
              {/* ── AI Status ── */}
              <div className="settings-section">
                <h3 style={{ marginTop: 0, marginBottom: '12px', fontSize: '1rem', fontWeight: 600 }}>AI Status</h3>

                <div style={{ marginBottom: '12px' }}>
                  {eventConfig.lex_enabled
                    ? statusBadge('Lex Enabled', '#2e7d32')
                    : statusBadge('Lex Disabled', '#666')}
                  {provisionStatus && (
                    isPending ? statusBadge('Provisioning…', '#1565c0')
                    : provisionStatus === 'ready' ? statusBadge('AI Ready', '#2e7d32')
                    : provisionStatus === 'error' ? statusBadge('Provision Error', '#c62828')
                    : statusBadge(provisionStatus, '#888')
                  )}
                </div>

                {provisionStatus === 'error' && eventConfig.ai_provisioning_error && (
                  <div className="error-message" style={{ marginBottom: '12px' }}>
                    {eventConfig.ai_provisioning_error}
                  </div>
                )}

                {isPending && (
                  <div style={{ color: '#1565c0', fontSize: '0.88rem', marginBottom: '12px' }}>
                    Provisioning in progress — checking every 5 seconds…
                  </div>
                )}

                {/* Resource ID table */}
                {!editingIds ? (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', marginBottom: '12px' }}>
                      <tbody>
                        {[
                          ['Lex Bot ID', eventConfig.lex_bot_id],
                          ['Lex Bot Alias ID', eventConfig.lex_bot_alias_id],
                          ['Lex Locale', eventConfig.lex_bot_locale_id || 'en_US (default)'],
                          ['Bedrock Agent ID', eventConfig.bedrock_agent_id],
                          ['Bedrock Agent Alias ID', eventConfig.bedrock_agent_alias_id],
                        ].map(([label, value]) => (
                          <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '5px 12px', color: '#666', width: '42%' }}>{label}</td>
                            <td style={{ padding: '5px 12px', fontFamily: 'monospace', fontSize: '0.8rem', color: value ? '#333' : '#aaa' }}>
                              {value || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button
                      onClick={() => setEditingIds(true)}
                      className="btn-refresh"
                      style={{ fontSize: '0.85rem' }}
                    >
                      Edit IDs Manually
                    </button>
                    {idsSuccess && <span style={{ marginLeft: '12px', color: '#2e7d32', fontSize: '0.85rem' }}>{idsSuccess}</span>}
                  </>
                ) : (
                  /* Edit IDs form */
                  <div>
                    {[
                      { key: 'lex_bot_id', label: 'Lex Bot ID' },
                      { key: 'lex_bot_alias_id', label: 'Lex Bot Alias ID' },
                      { key: 'lex_bot_locale_id', label: 'Lex Locale (blank = en_US)' },
                      { key: 'bedrock_agent_id', label: 'Bedrock Agent ID' },
                      { key: 'bedrock_agent_alias_id', label: 'Bedrock Agent Alias ID' },
                    ].map(({ key, label }) => (
                      <div className="form-group" key={key} style={{ marginBottom: '10px' }}>
                        <label className="form-label" style={{ fontSize: '0.85rem' }}>{label}</label>
                        <input
                          type="text"
                          value={idFields[key]}
                          onChange={(e) => setIdFields(prev => ({ ...prev, [key]: e.target.value }))}
                          className="form-input"
                          style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                          placeholder="Leave blank to clear"
                          disabled={savingIds}
                        />
                      </div>
                    ))}
                    {idsError && <div className="error-message" style={{ marginBottom: '10px' }}>{idsError}</div>}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                      <button onClick={handleSaveIds} disabled={savingIds} className="btn-primary" style={{ fontSize: '0.9rem' }}>
                        {savingIds ? 'Saving…' : 'Save IDs'}
                      </button>
                      <button onClick={() => { setEditingIds(false); setIdsError(null); }} className="btn-refresh" style={{ fontSize: '0.9rem' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Create Lex Bot ── */}
              {!hasLex && !isPending && (
                <div className="settings-section">
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>Create Lex Bot</h3>
                  <p style={{ margin: '0 0 14px', fontSize: '0.9rem', color: '#555' }}>
                    Provisions a Lex V2 bot for this event. Required before creating a Bedrock Agent.
                    This may take several minutes.
                  </p>
                  {!eventConfig.lex_enabled && (
                    <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#c62828' }}>
                      Lex must be enabled on the event (via Event Management) before provisioning.
                    </p>
                  )}
                  {provisionError && <div className="error-message" style={{ marginBottom: '10px' }}>{provisionError}</div>}
                  <button
                    onClick={() => handleProvision('lex')}
                    disabled={provisioning || !eventConfig.lex_enabled}
                    className="btn btn-secondary"
                  >
                    {provisioning ? 'Starting…' : 'Create Lex Bot'}
                  </button>
                </div>
              )}

              {/* ── Create Bedrock Agent ── */}
              {hasLex && !hasBedrock && !isPending && (
                <div className="settings-section">
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>Create Bedrock Agent</h3>
                  <p style={{ margin: '0 0 14px', fontSize: '0.9rem', color: '#555' }}>
                    Provisions a Bedrock Agent for this event. Requires the Lex bot to already be ready.
                    This may take several minutes.
                  </p>
                  {provisionError && <div className="error-message" style={{ marginBottom: '10px' }}>{provisionError}</div>}
                  <button
                    onClick={() => handleProvision('bedrock')}
                    disabled={provisioning}
                    className="btn btn-secondary"
                  >
                    {provisioning ? 'Starting…' : 'Create Bedrock Agent'}
                  </button>
                </div>
              )}

              {/* ── Create Both (if neither exists) ── */}
              {!hasLex && !hasBedrock && !isPending && eventConfig.lex_enabled && (
                <div style={{ marginTop: '-12px', marginBottom: '24px', paddingLeft: '2px' }}>
                  <button
                    onClick={() => handleProvision('all')}
                    disabled={provisioning}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.85rem', opacity: 0.8 }}
                  >
                    {provisioning ? 'Starting…' : 'Create Both (Lex + Bedrock)'}
                  </button>
                </div>
              )}

              {/* ── Agent Instruction ── */}
              {hasBedrock && (
                <div className="settings-section">
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>Agent Instruction</h3>
                  <p style={{ margin: '0 0 14px', fontSize: '0.9rem', color: '#555' }}>
                    Controls how the Bedrock Agent responds to guest questions. After saving, the agent
                    re-prepares — changes are live within a few minutes.
                  </p>
                  <div className="form-group">
                    <textarea
                      value={instruction}
                      onChange={(e) => setInstruction(e.target.value)}
                      rows={20}
                      className="form-input"
                      style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' }}
                      disabled={savingInstruction}
                      placeholder="Enter the agent instruction..."
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>
                        Saves immediately and triggers agent re-preparation.
                      </span>
                      <span style={{ fontSize: '12px', color: instruction.length > 3800 ? '#dc3545' : '#888', whiteSpace: 'nowrap', marginLeft: '16px' }}>
                        {instruction.length.toLocaleString()} chars
                      </span>
                    </div>
                  </div>
                  {instructionSuccess && <div className="success-message" style={{ marginBottom: '12px' }}>{instructionSuccess}</div>}
                  {instructionError && <div className="error-message" style={{ marginBottom: '12px' }}>{instructionError}</div>}
                  <button
                    onClick={handleSaveInstruction}
                    disabled={savingInstruction || !instruction.trim()}
                    className="btn-primary"
                  >
                    {savingInstruction ? 'Saving…' : 'Save Instruction'}
                  </button>
                </div>
              )}

              {/* ── Delete Resources ── */}
              {(hasLex || hasBedrock) && (
                <div className="settings-section">
                  <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>
                    Delete AI Resources
                  </h3>
                  <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#555' }}>
                    Permanently remove provisioned AWS resources. Delete Bedrock Agent before Lex if deleting both separately.
                  </p>

                  {isPending && (
                    <div className="error-message" style={{ marginBottom: '12px' }}>
                      Provisioning in progress. Wait for completion before deleting.
                    </div>
                  )}

                  {deleteResult && (
                    <div
                      className={deleteResult.success ? 'success-message' : 'error-message'}
                      style={{ marginBottom: '12px' }}
                    >
                      <strong>{deleteResult.success ? 'Deletion complete' : 'Deletion failed'}:</strong>{' '}
                      {deleteResult.message || deleteResult.error}
                    </div>
                  )}

                  {deleteResult?.details && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '16px' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Component</th>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(deleteResult.details).map(([key, value]) => (
                          <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '6px 12px', color: '#555' }}>{STEP_LABELS[key] || key}</td>
                            <td style={{ padding: '6px 12px', fontWeight: 600, color: stepColor(value), fontSize: '0.85rem' }}>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {!deprovisioning && !isPending && !confirmDelete && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {hasBedrock && (
                        <button
                          onClick={() => setConfirmDelete('bedrock')}
                          style={{ padding: '7px 16px', background: 'none', color: '#c62828', border: '1px solid #c62828', borderRadius: '4px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}
                        >
                          Delete Bedrock Agent
                        </button>
                      )}
                      {hasLex && (
                        <button
                          onClick={() => setConfirmDelete('lex')}
                          disabled={hasBedrock}
                          title={hasBedrock ? 'Delete Bedrock Agent first' : ''}
                          style={{ padding: '7px 16px', background: 'none', color: hasBedrock ? '#aaa' : '#c62828', border: `1px solid ${hasBedrock ? '#ccc' : '#c62828'}`, borderRadius: '4px', cursor: hasBedrock ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: 600 }}
                        >
                          Delete Lex Bot
                        </button>
                      )}
                      {hasLex && hasBedrock && (
                        <button
                          onClick={() => setConfirmDelete('all')}
                          style={{ padding: '7px 16px', background: 'none', color: '#c62828', border: '1px solid #c62828', borderRadius: '4px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}
                        >
                          Delete All AI Components
                        </button>
                      )}
                    </div>
                  )}

                  {deprovisioning && (
                    <div style={{ color: '#555', fontStyle: 'italic' }}>
                      Deleting… this may take up to a minute.
                    </div>
                  )}

                  {confirmDelete && (
                    <div style={{ marginTop: '16px', padding: '16px', background: '#fff8f8', border: '1px solid #f5c6c6', borderRadius: '6px' }}>
                      <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#c62828' }}>Confirm deletion</p>
                      <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#444' }}>
                        {deleteConfirmText[confirmDelete]} This cannot be undone.
                      </p>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={handleDeprovision}
                          style={{ padding: '8px 18px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
                        >
                          Confirm Delete
                        </button>
                        <button onClick={() => setConfirmDelete(null)} className="btn-refresh" style={{ fontSize: '0.9rem' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
